import { useState, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';

interface CollapsibleSectionProps {
  /** Optional HTML id for anchor scrolling (e.g., "energy-breakdown"). */
  id?: string;
  title: string;
  badge?: string | number;
  collapsedSummary: ReactNode;
  defaultExpanded?: boolean;
  children: ReactNode;
}

export function CollapsibleSection({
  id,
  title,
  badge,
  collapsedSummary,
  defaultExpanded = false,
  children,
}: CollapsibleSectionProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <div id={id} className="rounded-lg border border-border overflow-hidden">
      {/* Header — always visible */}
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 px-4 py-3 bg-wt-bg-tertiary hover:bg-wt-bg-tertiary/80 transition-colors text-left"
      >
        <ChevronDown
          className={`w-4 h-4 text-muted-foreground shrink-0 transition-transform duration-200 ${expanded ? 'rotate-0' : '-rotate-90'}`}
        />
        <span className="text-sm font-medium text-foreground">{title}</span>
        {badge != null && (
          <span className="text-xs font-mono px-2 py-0.5 rounded-full bg-wt-bg-secondary text-muted-foreground">
            {badge}
          </span>
        )}
        {/* Collapsed summary — hidden when expanded */}
        {!expanded && (
          <span className="ml-auto text-xs text-muted-foreground truncate max-w-[60%] text-right">
            {collapsedSummary}
          </span>
        )}
      </button>

      {/* Expandable content — CSS grid animation */}
      <div
        className="grid transition-[grid-template-rows] duration-200 ease-out"
        style={{ gridTemplateRows: expanded ? '1fr' : '0fr' }}
      >
        <div className="overflow-hidden">
          <div className="px-4 py-4 bg-wt-bg-secondary">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}
